/**
 * ContactManagementTour - Walks users through contact management in Voley.
 *
 * Covers: AMS sync, contacts page (search, filter, bulk message),
 * contact sidebar (editing, consent, tags, notes, manage conversation).
 */

import type { TourStep } from '../types';

/** Unique identifiers for each ContactManagementTour step */
export type ContactManagementTourStepId =
  | 'contacts_welcome'
  | 'highlight_contacts_page'
  | 'highlight_contacts_search'
  | 'highlight_sync_btn'
  | 'highlight_bulk_message'
  | 'highlight_contact_panel_btn'
  | 'highlight_contact_info'
  | 'highlight_consent'
  | 'highlight_tags'
  | 'highlight_notes'
  | 'highlight_manage_conversation_btn'
  | 'highlight_manage_conversation_expanded'
  | 'contacts_complete';

/** Ordered list of all ContactManagementTour steps */
export const CONTACT_MANAGEMENT_TOUR_STEPS: TourStep[] = [
  {
    id: 'contacts_welcome',
    type: 'modal',
    title: '',
    content: '',
  },
  {
    id: 'highlight_contacts_page',
    type: 'highlight',
    targetSelector: '[data-tour-id="contacts-page"]',
    title: 'Your Contact Directory',
    content:
      'This is your centralized contact directory. Every client who has ever messaged your agency lives here \u2014 automatically organized and always up to date.',
    placement: 'right',
    positionInside: true,
  },
  {
    id: 'highlight_contacts_search',
    type: 'highlight',
    targetSelector: '[data-tour-id="contacts-toolbar"]',
    title: 'Search, Sort & Filter',
    content:
      'Search for any contact by name, phone number, or email. Use "Sort & Filter" to organize by name or date, and filter by tags to quickly find the contacts that matter most.',
    placement: 'bottom',
  },
  {
    id: 'highlight_sync_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="sync-btn"]',
    title: 'AMS Contact Sync',
    content:
      'When your AMS is connected, contacts sync automatically. Whenever a client profile is updated in your AMS \u2014 new phone number, address change, name update \u2014 Voley reflects those changes in real time. No manual imports needed.',
    placement: 'bottom',
  },
  {
    id: 'highlight_bulk_message',
    type: 'highlight',
    targetSelector: '[data-tour-id="bulk-message-btn"]',
    title: 'Bulk Messaging',
    content:
      'Select multiple contacts using the checkboxes, then click this button to send a bulk message to all of them at once. Perfect for renewal reminders, policy updates, or agency-wide announcements.',
    placement: 'bottom',
  },
  {
    id: 'highlight_contact_panel_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="contact-panel-btn"]',
    title: 'Contact Details Panel',
    content:
      'Click this button to open the contact details sidebar. This panel gives you a complete view of any contact \u2014 their info, tags, notes, consent status, and conversation management tools.',
    placement: 'left',
    advanceOnElement: '[data-tour-id="contact-sidebar-open"]',
  },
  {
    id: 'highlight_contact_info',
    type: 'highlight',
    targetSelector: '[data-tour-id="contact-info-section"]',
    title: 'Contact Information',
    content:
      'View and edit contact details like email, company, address, and preferred language. Click the edit icon at the top of the sidebar to make changes. When your AMS is connected, this information stays in sync automatically.',
    placement: 'left',
  },
  {
    id: 'highlight_consent',
    type: 'highlight',
    targetSelector: '[data-tour-id="consent-section"]',
    title: 'Consent & Compliance',
    content:
      'Voley automatically tracks messaging consent. When a contact responds to your messages, they are marked as "Consented." If they text STOP, they\u2019re marked as opted out and you won\u2019t be able to message them until they text START again.',
    placement: 'top',
  },
  {
    id: 'highlight_tags',
    type: 'highlight',
    targetSelector: '[data-tour-id="tags-section"]',
    title: 'Tags & Organization',
    content:
      'Add tags to categorize contacts \u2014 by line of business, renewal month, VIP status, or anything your agency needs. Tags make it easy to filter and find contacts across your directory and conversations.',
    placement: 'left',
  },
  {
    id: 'highlight_notes',
    type: 'highlight',
    targetSelector: '[data-tour-id="notes-section"]',
    title: 'Internal Notes',
    content:
      'Add private notes to any contact for your team. Notes are internal \u2014 clients never see them. Use them for context like "prefers text over email" or "renews in March." Every note is timestamped with the author\u2019s name.',
    placement: 'left',
  },
  {
    id: 'highlight_manage_conversation_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="manage-conversation-section"]',
    title: 'Manage Conversation',
    content:
      'Click "Manage Conversation" to expand your conversation management tools.',
    placement: 'top',
    advanceOnElement: '[data-tour-id="manage-conversation-expanded"]',
  },
  {
    id: 'highlight_manage_conversation_expanded',
    type: 'highlight',
    targetSelector: '[data-tour-id="manage-conversation-expanded"]',
    title: 'Conversation Tools',
    content:
      'Here you can mute notifications, block contacts, archive or delete conversations, export conversation history, and get help. Everything you need to control and organize your client communications.',
    placement: 'top',
  },
  {
    id: 'contacts_complete',
    type: 'modal',
    title: '',
    content: '',
  },
];
